import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type {
  CompetitionSeasonView,
  RuleProfileCatalogEntryView,
  RuleProfileDetailView,
  RuleProfileSummaryView,
  SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { apiRequest } from './api';
import { formatCalendarDate } from './athletes';
import { errorKeyOf, type ErrorKey } from './BillingForms';
import {
  blockerKeys,
  canActivate,
  catalogEntriesFor,
  evaluateVerification,
  latestPlausibleDay,
  openDraft,
  profileStatusKeys,
  verificationProblemKeys,
} from './competitions';
import { categoryLabel } from './teams';

export interface RuleProfilesSectionProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}`. */
  base: string;
  csrfToken: string;
  competition: CompetitionSeasonView;
  catalog: readonly RuleProfileCatalogEntryView[];
  canManageRules: boolean;
  /** Called after a change so the competition list shows the new active version. */
  onChanged: () => Promise<void>;
}

/**
 * Rule profile versions of one competition season. A draft never approves anyone: a rules officer
 * records the hash of each source document and the day the sources were checked, then activates it,
 * which supersedes the version that was active. Active and superseded versions are read-only.
 */
export function RuleProfilesSection({
  locale,
  base,
  csrfToken,
  competition,
  catalog,
  canManageRules,
  onChanged,
}: RuleProfilesSectionProps): React.JSX.Element {
  const [profiles, setProfiles] = useState<RuleProfileSummaryView[] | null>(null);
  const [draft, setDraft] = useState<RuleProfileDetailView | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [catalogKey, setCatalogKey] = useState('');
  const [hashes, setHashes] = useState<Record<string, string>>({});
  const [checkedOn, setCheckedOn] = useState('');
  const [verificationNote, setVerificationNote] = useState('');
  const [activationNote, setActivationNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const load = useCallback(async () => {
    try {
      const loaded = await apiRequest<RuleProfileSummaryView[]>(
        `${base}/rule-profiles?competitionSeasonId=${encodeURIComponent(competition.id)}`,
      );
      setProfiles(loaded);
      const open = openDraft(loaded);
      setDraft(
        open === null
          ? null
          : await apiRequest<RuleProfileDetailView>(`${base}/rule-profiles/${open.id}`),
      );
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base, competition.id]);

  useEffect(() => {
    setProfiles(null);
    setDraft(null);
    setErrorKey(null);
    setMessage(null);
    setHashes({});
    setCheckedOn('');
    setVerificationNote('');
    setActivationNote('');
    setConfirmed(false);
    void load();
  }, [load]);

  const candidates = catalogEntriesFor(catalog, competition);
  const verification =
    draft === null
      ? null
      : evaluateVerification({ hashes, checkedOn, note: verificationNote }, latestPlausibleDay());

  async function change(
    action: () => Promise<unknown>,
    successKey: Parameters<typeof translate>[1],
  ): Promise<void> {
    setErrorKey(null);
    setMessage(null);
    try {
      await action();
      setMessage(translate(locale, successKey));
      await load();
      await onChanged();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  async function createDraft(): Promise<void> {
    await change(
      () =>
        apiRequest<RuleProfileDetailView>(`${base}/rule-profiles`, {
          method: 'POST',
          csrfToken,
          body: { competitionSeasonId: competition.id, catalogKey },
        }),
      'competitions.profiles.draftCreated',
    );
    setCatalogKey('');
  }

  async function recordVerification(current: RuleProfileDetailView): Promise<void> {
    if (verification?.request == null) return;
    const request = verification.request;
    await change(
      () =>
        apiRequest<RuleProfileDetailView>(`${base}/rule-profiles/${current.id}/verification`, {
          method: 'POST',
          csrfToken,
          ifMatchVersion: current.version,
          body: request,
        }),
      'competitions.verify.done',
    );
    setHashes({});
  }

  async function activate(current: RuleProfileDetailView): Promise<void> {
    await change(
      () =>
        apiRequest<RuleProfileDetailView>(`${base}/rule-profiles/${current.id}/activate`, {
          method: 'POST',
          csrfToken,
          ifMatchVersion: current.version,
          body: { note: activationNote.trim() },
        }),
      'competitions.activate.done',
    );
    setActivationNote('');
    setConfirmed(false);
  }

  return (
    <Box component="section" aria-labelledby="profiles-title" sx={{ mt: 2 }}>
      <Typography variant="h5" component="h2" id="profiles-title" gutterBottom>
        {translate(locale, 'competitions.profiles.title')}: {competition.name}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {message !== null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}
      {profiles !== null && profiles.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {translate(locale, 'competitions.profiles.empty')}
        </Typography>
      )}
      {profiles !== null && profiles.length > 0 && (
        <Stack
          component="ul"
          spacing={1}
          sx={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: 3 }}
        >
          {profiles.map((profile) => (
            <Box component="li" key={profile.id}>
              <Typography>
                {translate(locale, 'competitions.profile.version')} {profile.versionNumber} ·{' '}
                {translate(locale, profileStatusKeys[profile.status])} ·{' '}
                {categoryLabel(locale, profile.category)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {translate(locale, 'competitions.profile.validity')}:{' '}
                {formatCalendarDate(profile.validFrom, locale)} –{' '}
                {formatCalendarDate(profile.validTo, locale)} ·{' '}
                {translate(locale, 'competitions.profile.counts')
                  .replace('{sources}', String(profile.sourceCount))
                  .replace('{hashes}', String(profile.sourceHashCount))
                  .replace('{conditions}', String(profile.conditionCount))
                  .replace('{manual}', String(profile.mandatoryManualCheckCount))}
              </Typography>
              {profile.sourcesCheckedOn !== null && (
                <Typography variant="body2" color="text.secondary">
                  {translate(locale, 'competitions.profile.checkedOn')}:{' '}
                  {formatCalendarDate(profile.sourcesCheckedOn, locale)}
                </Typography>
              )}
              {profile.activatedAt !== null && (
                <Typography variant="body2" color="text.secondary">
                  {translate(locale, 'competitions.profile.activatedOn')}:{' '}
                  {formatCalendarDate(profile.activatedAt.slice(0, 10), locale)}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      )}
      {canManageRules && profiles !== null && draft === null && (
        <Stack
          component="form"
          spacing={2}
          sx={{ maxWidth: 480, mb: 3 }}
          onSubmit={(event) => {
            event.preventDefault();
            void createDraft();
          }}
        >
          <TextField
            required
            select
            size="small"
            label={translate(locale, 'competitions.profiles.catalogField')}
            value={catalogKey}
            onChange={(event) => {
              setCatalogKey(event.target.value);
            }}
          >
            {candidates.map((entry) => (
              <MenuItem key={entry.key} value={entry.key}>
                {categoryLabel(locale, entry.category)}
                {entry.division === null ? '' : ` · ${entry.division}`}
              </MenuItem>
            ))}
          </TextField>
          <Box>
            <Button type="submit" variant="contained" disabled={catalogKey === ''}>
              {translate(locale, 'competitions.profiles.createDraft')}
            </Button>
          </Box>
        </Stack>
      )}
      {draft !== null && (
        <Box component="section" aria-labelledby="draft-title">
          <Typography variant="subtitle1" component="h3" id="draft-title" gutterBottom>
            {translate(locale, 'competitions.profile.version')} {draft.versionNumber} ·{' '}
            {translate(locale, 'competitions.profile.status.draft')}
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            {translate(locale, 'competitions.profile.draftNote')}
          </Alert>
          {draft.blockers.length > 0 && (
            <Box component="ul" sx={{ mt: 0, mb: 2 }}>
              {draft.blockers.map((blocker) => (
                <li key={blocker}>{translate(locale, blockerKeys[blocker])}</li>
              ))}
            </Box>
          )}
          {draft.mandatoryManualChecks.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" component="h4">
                {translate(locale, 'competitions.profile.manualChecks')}
              </Typography>
              <Box component="ul" sx={{ mt: 0 }}>
                {draft.mandatoryManualChecks.map((check) => (
                  <li key={check.key}>{check.sourceClause}</li>
                ))}
              </Box>
            </Box>
          )}
          {canManageRules && (
            <Stack
              component="form"
              spacing={2}
              sx={{ maxWidth: 560, mb: 3 }}
              onSubmit={(event) => {
                event.preventDefault();
                void recordVerification(draft);
              }}
            >
              <Typography variant="subtitle2" component="h4">
                {translate(locale, 'competitions.verify.title')}
              </Typography>
              {draft.sources.map((source) => (
                <TextField
                  key={source.id}
                  size="small"
                  label={`${translate(locale, 'competitions.verify.hashField')} ${source.id}`}
                  value={hashes[source.id] ?? ''}
                  placeholder={source.sourceHash ?? ''}
                  onChange={(event) => {
                    setHashes({ ...hashes, [source.id]: event.target.value });
                  }}
                  slotProps={{ htmlInput: { spellCheck: false, autoComplete: 'off' } }}
                />
              ))}
              <TextField
                required
                size="small"
                type="date"
                label={translate(locale, 'competitions.verify.checkedOn')}
                value={checkedOn}
                onChange={(event) => {
                  setCheckedOn(event.target.value);
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                required
                size="small"
                label={translate(locale, 'competitions.verify.note')}
                value={verificationNote}
                onChange={(event) => {
                  setVerificationNote(event.target.value);
                }}
                slotProps={{ htmlInput: { maxLength: 500 } }}
              />
              {verification?.problem != null && (checkedOn !== '' || verificationNote !== '') && (
                <Typography variant="body2" color="text.secondary">
                  {translate(locale, verificationProblemKeys[verification.problem])}
                </Typography>
              )}
              <Box>
                <Button type="submit" variant="contained" disabled={verification?.request == null}>
                  {translate(locale, 'competitions.verify.submit')}
                </Button>
              </Box>
            </Stack>
          )}
          {canManageRules && (
            <Stack
              component="form"
              spacing={2}
              sx={{ maxWidth: 560 }}
              onSubmit={(event) => {
                event.preventDefault();
                void activate(draft);
              }}
            >
              <Typography variant="subtitle2" component="h4">
                {translate(locale, 'competitions.activate.title')}
              </Typography>
              <TextField
                required
                size="small"
                label={translate(locale, 'competitions.activate.note')}
                value={activationNote}
                onChange={(event) => {
                  setActivationNote(event.target.value);
                }}
                slotProps={{ htmlInput: { maxLength: 500 } }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={confirmed}
                    onChange={(event) => {
                      setConfirmed(event.target.checked);
                    }}
                  />
                }
                label={translate(locale, 'competitions.activate.confirm')}
              />
              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  color="warning"
                  disabled={!canActivate(draft) || !confirmed || activationNote.trim() === ''}
                >
                  {translate(locale, 'competitions.activate.submit')}
                </Button>
              </Box>
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
}
